// libraries
import React, { useCallback, useMemo, useState, useEffect } from "react";
import clsx from "clsx";

// components
import Section from "/react4xp/components/Section";
import Button from "/react4xp/components/Button";

// utils
import { isNonWhitespaceString } from "/lib/reactutils/utils";

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface EventItem {
  id?: string;
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  hours?: string;
  city?: string;
  groupLabel?: string;
  href?: string;
  target?: "_self" | "_blank";
}

export interface EventsProps {
  title?: string;
  description?: string;
  heading?: string;
  view?: "limited" | "full";
  events?: EventItem[];
  ctaHref?: string;
  ctaText?: string;
  toggleHref?: string;
  toggleTarget?: "_self" | "_blank";
  toggleText?: string;
  nextPageUrl?: string;
  onLoadMore?: (nextPageUrl: string) => void | Promise<void>;
  loadMoreText?: string;
}

interface NormalizedEvent {
  key: string;
  title: string;
  description?: string;
  startDate: Date;
  endDate: Date;
  monthKey: string;
  monthLabel: string;
  hours?: string;
  city?: string;
  href?: string;
  target?: "_self" | "_blank";
}

interface EventsResponse {
  events?: EventItem[];
  nextPageUrl?: string;
}

// ============================================================================
// Constants & Formatters
// ============================================================================

const dayFormatter = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  timeZone: "UTC",
});
const monthFormatter = new Intl.DateTimeFormat("nb-NO", {
  month: "long",
  timeZone: "UTC",
});
const monthShortFormatter = new Intl.DateTimeFormat("nb-NO", {
  month: "short",
  timeZone: "UTC",
});

// ============================================================================
// Utility Functions
// ============================================================================

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1);

const parseDate = (value?: string): Date | null => {
  if (!isNonWhitespaceString(value)) {
    return null;
  }

  // If the format is YYYY-MM-DD, we parse it as UTC manually.
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (match) {
    const [_, y, m, d] = match.map(Number);
    return new Date(Date.UTC(y, m - 1, d));
  }

  // Otherwise fallback to normal parsing (strings with time)
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const createMonthKey = (date: Date) =>
  `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;

const normalizeEvents = (events: EventItem[]): NormalizedEvent[] => {
  return (Array.isArray(events) ? events : [])
    .map<NormalizedEvent | null>((item, index) => {
      const startDate = parseDate(item?.startDate);
      if (!startDate) {
        return null;
      }
      const endDate = parseDate(item?.endDate) || startDate;
      const inferredLabel = capitalize(monthFormatter.format(startDate));
      const customLabel = isNonWhitespaceString(item?.groupLabel)
        ? (item!.groupLabel as string).trim()
        : undefined;
      const monthLabel = customLabel ?? inferredLabel;
      return {
        key: item?.id ?? `${index}-${startDate.toISOString()}`,
        title: isNonWhitespaceString(item?.title)
          ? (item!.title as string).trim()
          : "Uten tittel",
        description: isNonWhitespaceString(item?.description)
          ? (item!.description as string).trim()
          : undefined,
        startDate,
        endDate,
        monthKey: createMonthKey(startDate),
        monthLabel,
        hours: isNonWhitespaceString(item?.hours)
          ? (item!.hours as string).trim()
          : undefined,
        city: isNonWhitespaceString(item?.city)
          ? (item!.city as string).trim()
          : undefined,
        href: item?.href,
        target: item?.target,
      };
    })
    .filter((item): item is NormalizedEvent => Boolean(item));
};

const groupEventsByMonth = (events: NormalizedEvent[]) => {
  const map = new Map<string, { label: string; events: NormalizedEvent[] }>();

  events.forEach((event) => {
    const existing = map.get(event.monthKey);
    if (existing) {
      existing.events.push(event);
    } else {
      map.set(event.monthKey, {
        label: event.monthLabel,
        events: [event],
      });
    }
  });

  return Array.from(map.entries()).map(([key, value]) => {
    const [yearString] = key.split("-");
    const year = Number(yearString);

    return {
      monthKey: key,
      label: value.label,
      year: Number.isFinite(year) ? year : undefined,
      events: value.events,
    };
  });
};

// ============================================================================
// Components
// ============================================================================

const EventCard: React.FC<{ event: NormalizedEvent }> = ({ event }) => {
  const hasRange = event.endDate.getTime() !== event.startDate.getTime();
  const startDay = dayFormatter.format(event.startDate);
  const endDay = dayFormatter.format(event.endDate);
  const startMonthShort = monthShortFormatter.format(event.startDate);
  const endMonthShort = monthShortFormatter.format(event.endDate);
  const startMonthFull = monthFormatter.format(event.startDate);
  const endMonthFull = monthFormatter.format(event.endDate);
  const startDayNumber = Number(startDay);
  const endDayNumber = Number(endDay);
  const startYear = event.startDate.getFullYear();
  const endYear = event.endDate.getFullYear();
  const sameYear = startYear === endYear;

  const ensureTrailingPeriod = (value: string) =>
    value.endsWith(".") ? value : `${value}.`;
  const startMonthLabelDesktop = hasRange
    ? ensureTrailingPeriod(startMonthShort)
    : startMonthFull;
  const endMonthLabelDesktop = ensureTrailingPeriod(endMonthShort);

  // Mobile label: always include year
  let mobileDateLabel = `${startDayNumber}. ${startMonthFull} ${startYear}`;
  if (hasRange) {
    if (sameYear) {
      const sameMonth = startMonthFull === endMonthFull;
      mobileDateLabel = sameMonth
        ? `${startDayNumber}.–${endDayNumber}. ${startMonthFull} ${endYear}`
        : `${startDayNumber}. ${startMonthFull}–${endDayNumber}. ${endMonthFull} ${endYear}`;
    } else {
      mobileDateLabel = `${startDayNumber}. ${startMonthFull} ${startYear}–${endDayNumber}. ${endMonthFull} ${endYear}`;
    }
  }

  return (
    <article className="events__card">
      {event.href && (
        <a
          href={event.href}
          target={event.target}
          className="events__card-link"
          aria-label={event.title}
        />
      )}
      <div className="events__card-body">
        <div className="events__card-content">
          <h3 className="events__card-title">{event.title}</h3>
          {isNonWhitespaceString(event.description) && (
            <p
              className="events__card-description"
              dangerouslySetInnerHTML={{ __html: event.description }}
              suppressHydrationWarning
            />
          )}
        </div>
        <div className="events__card-meta">
          {Number.isFinite(startDayNumber) && (
            <div
              className="events__card-date-mobile"
              aria-label="Dato for arrangement"
            >
              <span className="events__card-date-mobile-range">
                {mobileDateLabel}
              </span>
            </div>
          )}
          {isNonWhitespaceString(event.hours) && (
            <span className="events__meta-item">{event.hours}</span>
          )}
          {isNonWhitespaceString(event.city) && (
            <span className="events__meta-item events__meta-item--location">
              <svg
                width="15"
                height="17"
                viewBox="0 0 15 17"
                fill="#fff"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M11.8606 2.42418C14.4856 5.00585 14.4856 9.13752 11.8956 11.685L7.16977 16.3334L2.44227 11.685C1.82745 11.0843 1.33891 10.3667 1.00536 9.57445C0.671821 8.7822 0.5 7.93128 0.5 7.07168C0.5 6.21209 0.671821 5.36117 1.00536 4.56892C1.33891 3.77668 1.82745 3.0591 2.44227 2.45835L2.47727 2.42418C3.7274 1.19124 5.41267 0.5 7.16852 0.5C8.92437 0.5 10.6105 1.19124 11.8606 2.42418ZM11.8606 2.42418C11.8256 2.38918 11.8256 2.38918 11.8606 2.42418ZM8.83393 6.33335C8.83393 6.77538 8.65834 7.1993 8.34578 7.51186C8.03322 7.82442 7.6093 8.00002 7.16727 8.00002C6.72524 8.00002 6.30132 7.82442 5.98876 7.51186C5.6762 7.1993 5.5006 6.77538 5.5006 6.33335C5.5006 5.89132 5.6762 5.4674 5.98876 5.15484C6.30132 4.84228 6.72524 4.66668 7.16727 4.66668C7.6093 4.66668 8.03322 4.84228 8.34578 5.15484C8.65834 5.4674 8.83393 5.89132 8.83393 6.33335Z"
                  stroke="#5C0120"
                  fill="transparent"
                  strokeMiterlimit="10"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {event.city}
            </span>
          )}
        </div>
      </div>

      <div className="events__card-date" aria-label="Dato for arrangement">
        <div className="events__card-day-group">
          <div className="events__card-day-group-inner">
            <div className="events__card-day-start">
              <span className="events__card-day">{startDay}</span>
              <span className="events__card-month">
                {startMonthLabelDesktop}
              </span>
              {(!hasRange || !sameYear) && (
                <span className="events__card-year">{startYear}</span>
              )}
            </div>
            {hasRange && (
              <>
                <span className="events__card-separator"></span>
                <div className="events__card-day-end">
                  <span className="events__card-day">{endDay}</span>
                  <span className="events__card-month">
                    {endMonthLabelDesktop}
                  </span>
                  <span className="events__card-year">{endYear}</span>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

const Events: React.FC<EventsProps> = ({
  title,
  description,
  heading,
  view = "full",
  events: initialEvents = [],
  ctaHref,
  ctaText = "Se alle",
  toggleHref,
  toggleTarget,
  toggleText,
  nextPageUrl: initialNextPageUrl,
  onLoadMore,
  loadMoreText,
}) => {
  const [events, setEvents] = useState<EventItem[]>(initialEvents);
  const [nextPageUrl, setNextPageUrl] = useState<string | undefined>(
    initialNextPageUrl
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setEvents(initialEvents);
    setNextPageUrl(initialNextPageUrl);
  }, [initialEvents, initialNextPageUrl]);

  const introTitle = isNonWhitespaceString(title)
    ? (title as string).trim()
    : undefined;
  const introDescription = isNonWhitespaceString(description)
    ? (description as string).trim()
    : undefined;
  const headingContent = isNonWhitespaceString(heading)
    ? (heading as string).trim()
    : undefined;

  const normalizedEvents = useMemo(() => normalizeEvents(events), [events]);
  const isFullView = view === "full";
  const groups = useMemo(
    () => (isFullView ? groupEventsByMonth(normalizedEvents) : []),
    [normalizedEvents, isFullView]
  );

  const hasEvents = isFullView
    ? groups.length > 0
    : normalizedEvents.length > 0;

  const trimmedNextPageUrl = isNonWhitespaceString(nextPageUrl)
    ? (nextPageUrl as string).trim()
    : undefined;
  const loadMoreLabel = isNonWhitespaceString(loadMoreText)
    ? (loadMoreText as string).trim()
    : "Last flere";

  const handleLoadMore = useCallback(async () => {
    if (isLoading || !trimmedNextPageUrl) return;

    setIsLoading(true);
    try {
      // If onLoadMore callback is provided, use it instead of fetching
      if (onLoadMore) {
        await Promise.resolve(onLoadMore(trimmedNextPageUrl));
        return;
      }

      // Otherwise, fetch the data directly
      const response = await fetch(trimmedNextPageUrl);
      if (!response.ok) {
        throw new Error("Failed to load more events");
      }
      const data: EventsResponse = await response.json();

      if (data.events && Array.isArray(data.events)) {
        setEvents((prevEvents) => [...prevEvents, ...data.events!]);
        setNextPageUrl(data.nextPageUrl);
      }
    } catch (error) {
      console.error("Error loading more events:", error);
    } finally {
      setIsLoading(false);
    }
  }, [isLoading, trimmedNextPageUrl, onLoadMore]);

  const shouldRenderHeading = isFullView && Boolean(headingContent);
  const shouldRenderToggle = Boolean(toggleHref && toggleText);
  const shouldRenderLoadMore = isFullView && Boolean(trimmedNextPageUrl);
  const shouldRenderListTitle = !isFullView && Boolean(introTitle);
  const shouldRenderHeader =
    isFullView && (shouldRenderHeading || shouldRenderToggle);
  const shouldRenderCta = !isFullView && Boolean(ctaHref);
  const shouldRenderEmptyText = !hasEvents;

  return (
    <Section className="events">
      <div className="container">
        <div className="row">
          <div className="col-12 col-lg-10 offset-lg-1">
            {(introTitle || introDescription) && isFullView && (
              <div className="events__intro">
                {introTitle && (
                  <h1
                    dangerouslySetInnerHTML={{ __html: introTitle }}
                    suppressHydrationWarning
                  />
                )}
                {introDescription && (
                  <p
                    dangerouslySetInnerHTML={{ __html: introDescription }}
                    suppressHydrationWarning
                  />
                )}
              </div>
            )}

            {shouldRenderHeader && headingContent && (
              <div className="events__header">
                {shouldRenderHeading && (
                  <div className="events__heading">
                    <h2
                      dangerouslySetInnerHTML={{
                        __html: headingContent as string,
                      }}
                      suppressHydrationWarning
                    />
                  </div>
                )}
              </div>
            )}

            {isFullView ? (
              <div className="events__month-group">
                {shouldRenderToggle && (
                  <a
                    href={toggleHref}
                    target={toggleTarget}
                    className="events__toggle button button--solid button--primary"
                  >
                    {toggleText}
                  </a>
                )}
                {groups.map((group) => (
                  <div key={group.monthKey} className="events__month">
                    <h3 className="events__month-title">
                      {group.label} {group.year}
                    </h3>
                    <div className="events__list">
                      {group.events.map((event) => (
                        <EventCard key={event.key} event={event} />
                      ))}
                    </div>
                  </div>
                ))}
                {shouldRenderEmptyText && (
                  <p className="events__empty">Ingen arrangementer</p>
                )}
              </div>
            ) : (
              <div className="events__list">
                {shouldRenderToggle && (
                  <a
                    href={toggleHref}
                    target={toggleTarget}
                    className="events__toggle button button--solid button--primary"
                  >
                    {toggleText}
                  </a>
                )}
                {shouldRenderListTitle && (
                  <div className="events__heading events__heading--limited">
                    <h2
                      dangerouslySetInnerHTML={{ __html: introTitle as string }}
                      suppressHydrationWarning
                    />
                  </div>
                )}
                {normalizedEvents.map((event) => (
                  <EventCard key={event.key} event={event} />
                ))}
                {shouldRenderEmptyText && (
                  <p className="events__empty">Ingen arrangementer</p>
                )}
              </div>
            )}

            {shouldRenderLoadMore && (
              <div
                className={clsx(
                  "events__actions",
                  "events__actions--load-more"
                )}
              >
                <Button
                  onClick={handleLoadMore}
                  style="solid"
                  color="primary"
                  small
                  loading={isLoading}
                >
                  {loadMoreLabel}
                </Button>
              </div>
            )}

            {shouldRenderCta && (
              <div className="events__cta">
                <Button href={ctaHref} style="solid" color="primary" small>
                  {ctaText}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

export default Events;
